import { useEffect, useState } from "react";

import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { useWindowDimensions } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { AnalyticsFieldName } from "@/constants/analytics/Analytics";
import { DeepLinkField } from "@/constants/analytics/DeepLinking";
import {
  WatchTrackingAnalyticsEvent,
  WatchTrackingAnalyticsFieldName,
} from "@/constants/analytics/WatchTracking";
import { router } from "@/crossPlatform/expo-router";
import useEncodedSearchParams from "@/hooks/useEncodedSearchParams";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import { useAnalytics } from "@/services/Analytics";
import { captureException } from "@/services/AppMonitor";
import { useToastDispatch } from "@/store/reducers/toast";
import type { VideoNaturalSize } from "@/ui/composed/media/videoPlayer/adapter/ExpoVideoPlayer";
import VideoPlayer from "@/ui/composed/media/videoPlayer/VideoPlayer";
import ModalTitleHeader from "@/ui/composed/modal/ModalTitleHeader";
import { HStack } from "@/ui/layout";
import ModalLayout from "@/ui/layout/ModalLayout";
import {
  ModalLayoutDimensions,
  WebModalConstraints,
} from "@/ui/layout/ModalTypes";
import { isWeb } from "@/utils/platform.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import { joinTestIDs } from "@/utils/string.util";

const baseTestID = "video-player-modal";
const AspectRatio = 0.5625;
const DefaultTitle = "Video";
const FallbackVideoResourcesPrefixUrl =
  "https://equip-media-prod.s3-accelerate.amazonaws.com/resources/videos/";

// landscape videos use a fixed 16:9 width-pinned sizing; portrait videos fill the
// available vertical space (window minus header/safe area), with width derived from
// natural aspect and centered horizontally by the parent HStack.
const useVideoPlayerSize = (
  videoSize: undefined | VideoNaturalSize,
): SizeDimensions => {
  const { top: topInset } = useSafeAreaInsets();
  const { height: windowHeight, width: windowWidth } = useWindowDimensions();

  const webConstraints = WebModalConstraints.normal;
  const [maxHeight, maxWidth, topOffset] = isWeb()
    ? [
        webConstraints.maxHeight ?? windowHeight,
        Math.min(windowWidth, webConstraints.maxWidth ?? windowWidth),
        0,
      ]
    : [windowHeight, windowWidth, topInset];

  const isPortrait = !isNil(videoSize) && videoSize.height > videoSize.width;
  if (!isPortrait) {
    return { height: maxWidth * AspectRatio, width: maxWidth };
  }

  const availableHeight =
    maxHeight - ModalLayoutDimensions.HeaderHeight - topOffset;
  const aspectWidthOverHeight = videoSize.width / videoSize.height;
  const idealWidth = availableHeight * aspectWidthOverHeight;

  const playerWidth = Math.min(idealWidth, maxWidth);
  const playerHeight =
    playerWidth === idealWidth
      ? availableHeight
      : playerWidth / aspectWidthOverHeight;
  return { height: playerHeight, width: playerWidth };
};

const getVideoPlayerLabel = (componentSource?: string) =>
  isNil(componentSource) ? "VideoPlayer" : `${componentSource} VideoPlayer`;

const handleClose = () => router.dismiss();

interface InvalidUrlError {
  toastText: string;
  urlError: string;
}

const useIsValidVideoUrl = ({
  componentSource,
  isDeepLink,
  url,
}: {
  componentSource?: string;
  isDeepLink: boolean;
  url?: string;
}): InvalidUrlError | undefined => {
  const playerLabel = getVideoPlayerLabel(componentSource);
  // on web, every route is user-addressable via the URL bar, so there is no
  // "trusted, came-from-in-app-prop" guarantee on the `url` param anymore —
  // validate its domain the same way we do for native deep links.
  const shouldEnforceStrictDomain = isDeepLink || isWeb();
  const deepLinkError = useIsValidDeepLinkVideoUrl({ componentSource, url });

  if (isEmpty(url)) {
    return {
      toastText:
        "Unable to open the provided video link. The video URL is missing or malformed",
      urlError: `${playerLabel} opened with missing url '${url}'`,
    };
  }

  return shouldEnforceStrictDomain ? deepLinkError : undefined;
};

const useIsValidDeepLinkVideoUrl = ({
  componentSource,
  url,
}: {
  componentSource?: string;
  url?: string;
}): InvalidUrlError | undefined => {
  const { appVideoResourcesPrefixUrl } = useFeatureFlags();
  // initial fetch of feature flags will typically return the empty string "" fallback value
  // in the case of deep links opening the app when it's been closed, this otherwise results in "invalid video URL domain"
  const prefixUrl = isNotNilNotEmpty(appVideoResourcesPrefixUrl)
    ? appVideoResourcesPrefixUrl
    : FallbackVideoResourcesPrefixUrl;

  const playerLabel = getVideoPlayerLabel(componentSource);
  if (isEmpty(prefixUrl)) {
    return {
      toastText:
        "Unable to open the provided video link. Videos are currently experiencing issues.",
      urlError: `${playerLabel} opened with url '${url}' but valid URL domain is empty`,
    };
  }

  // ensure trailing slash to protect against partial matches (eg https://{prefix}-some-other-url.com/video)
  const validDomain = prefixUrl.endsWith("/") ? prefixUrl : `${prefixUrl}/`;
  const isSupportedUrl = !isEmpty(validDomain) && url?.startsWith(validDomain);
  if (isSupportedUrl) return undefined;

  // mobile-app content video URLs are provided either 1) hardcoded in the app, or 2) via the equip backend.
  // deeplink URLs however can be arbitrarily provided as URL params and need further validation
  return {
    toastText:
      "Unable to open the provided video link. The video URL is not from a supported website domain.",
    urlError: `${playerLabel} opened with url from unsupported domain '${url}'`,
  };
};

const Modal = () => {
  const { track } = useAnalytics();
  const [videoSize, setVideoSize] = useState<undefined | VideoNaturalSize>(
    undefined,
  );
  const playerSize = useVideoPlayerSize(videoSize);
  const { setOrphanToastRequest } = useToastDispatch();
  const {
    carouselIndex: carouselIndexStr,
    carouselTitle,
    componentSource,
    deepLinkUrl,
    title: titleParam,
    url,
  } = useEncodedSearchParams<{
    carouselIndex?: string;
    carouselTitle?: string;
    componentSource?: string;
    deepLinkUrl?: string;
    title?: string;
    url?: string;
  }>();
  const carouselIndex = carouselIndexStr
    ? Number.parseInt(carouselIndexStr)
    : undefined;

  const title = titleParam ?? DefaultTitle;
  const isDeepLink = !isEmpty(deepLinkUrl);
  useEffect(() => {
    track(WatchTrackingAnalyticsEvent.Video.Opened, {
      [AnalyticsFieldName.Component]: componentSource,
      [AnalyticsFieldName.Title]: title,
      [AnalyticsFieldName.URL]: url,
      [DeepLinkField.IsDeepLink]: isDeepLink,
      [WatchTrackingAnalyticsFieldName.Video.CarouselIndex]: carouselIndex,
      [WatchTrackingAnalyticsFieldName.Video.CarouselTitle]: carouselTitle,
    });
  }, [
    carouselIndex,
    carouselTitle,
    componentSource,
    isDeepLink,
    title,
    track,
    url,
  ]);

  const videoUrlError = useIsValidVideoUrl({
    componentSource,
    isDeepLink,
    url,
  });
  if (!isNil(videoUrlError)) {
    const { toastText, urlError } = videoUrlError;
    captureException(new Error(`${urlError} with title '${titleParam}'`));
    // since deliberately dismissing this before first render, need to use an orphan toast
    setOrphanToastRequest({ text: toastText, variant: "error" });
    router.dismiss();
    return null;
  }

  const playerLabel = getVideoPlayerLabel(componentSource);
  const handlePlayerError = (error: string) => {
    captureException(
      new Error(
        `${playerLabel} received unhandled error '${error}' when opening url '${url}' with title '${titleParam}'`,
      ),
    );
  };

  return (
    <ModalLayout
      renderHeader={() => (
        <ModalTitleHeader
          navigationType="close"
          onDismiss={handleClose}
          testID={baseTestID}
          title={title}
        />
      )}
    >
      <HStack justifyContent="center" width="100%">
        <VideoPlayer
          carouselIndex={carouselIndex}
          carouselTitle={carouselTitle}
          componentSource={componentSource}
          onError={handlePlayerError}
          onVideoSize={setVideoSize}
          shouldAutoPlay
          size={playerSize}
          testID={joinTestIDs(baseTestID, "video-player")}
          title={title}
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- validated above with useIsValidVideoUrl
          videoUrl={url!}
        />
      </HStack>
    </ModalLayout>
  );
};

export { AspectRatio, DefaultTitle };
export default Modal;
